import type { OverviewTopicWeeklyRow } from "@nexus/contracts";

/** Number of top concepts (series) the overtime chart renders. */
export const TOP_N = 5;

export interface OvertimeWeek {
  /** ISO week_start (YYYY-MM-DD) this column represents. */
  weekStart: string;
  /** Display label — "Week 1" is the oldest week. */
  label: string;
  /** Asset count per top topic for this week (single bucket). */
  values: Record<string, number>;
}

export interface OvertimeData {
  topTopics: string[];
  weeks: OvertimeWeek[];
}

/**
 * Share of week as the overtime chart plots it (`toFixed(1)`), not `Math.round`.
 * 3 of 2,000 assets is 0.1 on the bars and must export as 0.1, not 0.2.
 */
export function shareOfWeekPercent(raw: number, weekTotal: number): number {
  if (weekTotal <= 0) return 0;
  return +((raw / weekTotal) * 100).toFixed(1);
}

/**
 * Pivot weekly topic rows into the per-week series the overtime chart renders.
 *
 * Scoped to a single `bucket` (default caller passes "you" — the tenant's own brand): both the
 * top-N ranking and the per-week values reflect only that brand, matching the single-brand Retool
 * tile. Weeks are ordered chronologically (ascending), so "Week 1" is the OLDEST week.
 */
export function buildOvertimeData(
  rows: OverviewTopicWeeklyRow[],
  bucket: OverviewTopicWeeklyRow["bucket"],
): OvertimeData {
  const scoped = rows.filter((r) => r.bucket === bucket);

  // ── Rank topics by total assets within the bucket ──
  const assetsByTopic = new Map<string, number>();
  for (const r of scoped) {
    assetsByTopic.set(r.Topic_Title, (assetsByTopic.get(r.Topic_Title) ?? 0) + (Number(r.Assets) || 0));
  }
  const topTopics = [...assetsByTopic.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_N)
    .map(([topic]) => topic);

  // ── Pivot into per-week buckets, keyed by week_start ──
  const topSet = new Set(topTopics);
  const weekMap = new Map<string, Record<string, number>>();
  for (const r of scoped) {
    if (!topSet.has(r.Topic_Title)) continue;
    let week = weekMap.get(r.week_start);
    if (!week) {
      week = {};
      weekMap.set(r.week_start, week);
    }
    week[r.Topic_Title] = (week[r.Topic_Title] ?? 0) + (Number(r.Assets) || 0);
  }

  // Chronological — oldest week first, labeled "Week 1" (matches Retool's left-to-right order).
  const sortedWeeks = [...weekMap.keys()].sort();
  const weeks: OvertimeWeek[] = sortedWeeks.map((weekStart, i) => ({
    weekStart,
    label: `Week ${i + 1}`,
    values: weekMap.get(weekStart)!,
  }));

  return { topTopics, weeks };
}
